/* eslint-disable react/jsx-no-literals */
import FuelType from '^enums/model/FuelType';
import ColorMaterialType from '^enums/model/ColorMaterialType';
import GearType from '^enums/model/GearType';
import ColorType from '^enums/model/ColorType';
import EmissionLabel from '^common/EmissionLabel';

const DK = {
  units: {
    kilometers: 'km',
    consumption: 'km/l',
    electricConsumption: 'Wh/km',
    emission: 'g/km',
    batteryCapacity: 'kWh',
  },
  moneyUnits: {
    default: 'kr.',
    pricePerMonth: 'kr. pr. md',
  },
  header: {
    title: 'priser og specifikationer',
  },
  steps: {
    campaignVariants: {
      title: 'Fremhævede modeller',
    },
    variants: {
      title: 'Varianter',
      legend: {
        campaignVariant: 'Fremhævede modeller',
      },
      disclaimer: (url: string): string => `Ekstra kilometer kan tilkøbes på alle modeller. Kontakt venligst din forhandler som kan give dig et tilbud der passer til dit behov. Eller klik selv ind på vores Online beregner for Privatleasing på ${url}`,
    },
    technicalParameters: {
      title: 'Tekniske specifikationer',
      categories: {
        engine: 'Motor',
        battery: 'Batteri',
        fuelConsumption: 'Brændstofforbrug (km/l- WLTP-norm)',
        performance: 'Ydeevne',
        dimensions: 'Dimensioner',
        dimensionsExterior: 'Dimensioner udvendige',
        dimensionsInterior: 'Dimensioner indvendige',
        volumes: 'Volumener',
        weights: 'Vægte',
        weightsAndTrackVolume: 'Vægte & Bagagerumsvolume',
        brakes: 'Bremser',
      },
      specifications: {
        cylindersVolume: (
          <>
            Cylindervolumen (cm<sup>3</sup>)
          </>
        ),
        cylindersAmount: 'Cylindre',
        maxPowerCee: 'Max. effekt CEE KW',
        maxHorsepowerCombinedCombustion: 'Max. Effekt kombineret forbrændnings',
        maxHorsepowerElectricEngine: 'Max. Effekt elektriskmotor HK for/bag',
        maxHorsepowerAtRevolutionsPerMinute: 'Max. effekt HK ved omdr./min',
        maxTorque: 'Max. drejningsmoment Nm',
        hasStartStop: 'Start & Stop',
        batteryCapacity: 'Kapacitet (kWh)',
        chargingCable: 'Ladekabel',
        chargingTimeAlternatingCurrent: 'Ladetid AC 11 kW (0-100%) ',
        chargingTimeDirectCurrent: 'Ladetid DC 100 kW (0-80%) ',
        energyConsumption: 'Energiforbrug (Wh/km)',
        batteryMaxPower: 'Maksimal ydelse (kW) ',
        onBoardCharger: 'OBC (On board charger)',
        plugType: 'Stiktype',
        batteryType: 'Type',
        tractionWheels: 'Trækkende hjul',
        drivingRange: 'Rækkevidde (km)',
        mixedDrivingConsumption: 'Blandet kørsel (km/l)',
        cityDrivingConsumption: 'Bykørsel (km/l)',
        highwayDrivingConsumption: 'Landevejskørsel (km/l)',
        co2Emission: (
          <>
            <EmissionLabel />-udledning g/km
          </>
        ),
        acceleration: 'Acceleration fra 0-100 km/t (sek)',
        maxSpeed: 'Tophastighed (km/t)',
        heightWithRoofRails: 'Højde m. tagrails (mm)',
        heightWithoutRoofRails: 'Højde u. tagrails (mm)',
        height: 'Højde (mm)',
        length: 'Længde (mm)',
        tireSize: 'Standard dækdimension',
        wheelbase: 'Akselafstand (mm)',
        exteriorWidthWithMirrors: 'Bredde (ekskl. sidespejle) (mm)',
        exteriorWidthWithoutMirrors: 'Bredde (inkl. sidespejle) (mm)',
        frontSeatsLength: 'Justeringslængde af forsæder (mm)',
        frontInteriorWidth: 'Bredde, for (mm)',
        rearInteriorWidth: 'Bredde, bag (mm)',
        fuelTankCapacity: 'Brændstoftankens kapacitet (liter)',
        trunkVolumeWithRearSeatsDown: 'Bagagerumsvolumen, bagsæder lagt ned (liter)',
        trunkVolumeWithRearSeatsUp: 'Bagagerumsvolumen, bagsæder oppe (liter)',
        maxRoofLoad: 'Max. Taglast (kg)',
        maxWeightOfTrailerCoupling: 'Max. vægt på anhængerkobling',
        readyWeight: 'Køreklar vægt (kg)',
        totalWeight: 'Totalvægt (kg)',
        truckWeight: 'Vogntogsvægt (kg)',
        approvedPayload: 'Typegodkendt nyttelast',
        approvedTrailerWeightWithBrakes: 'Typegodkendt anhængervægt med bremser',
        approvedTrailerWeightWithoutBrakes: 'Typegodkendt anhængervægt uden bremser',
        maxTrailerWeightWithBrakes: 'Maks. Anhængervægt m. bremser',
        maxTrailerWeightWithoutBrakes: 'Maks. Anhængervægt u. bremse',
        frontBrakesType: 'Forbremse',
        rearBrakesType: 'Bagbremse',
      },
    },
    colors: {
      title: 'Farver',
      materialType: {
        [ColorMaterialType.Metallic]: 'Metallak',
        [ColorMaterialType.Standard]: 'Standardlak',
        [ColorMaterialType.Special]: 'Speciel lak',
        [ColorMaterialType.Pastel]: 'Pastellak',
        [ColorMaterialType.Unilack]: 'Unilak',
      },
    },
    trims: {
      title: 'Udstyr',
      highlighted: 'Fremhævet udstyr',
    },
    chargingSubscriptions: {
      title: 'Ladeløsninger',
      subscription: 'Abonnement',
      installation: 'Installation',
    },
    accessories: {
      title: 'Tilbehør',
      price: 'Pris',
    },
    interiors: {
      /**
       * TODO
       * Request translation
       */
      title: 'Interiør',
    },
  },
  common: {
    tableLegends: {
      included: 'Inkluderet',
      notPossible: 'Ikke tilgængelig',
      optional: 'Optional',
    },
    errorPage: {
      title: 'Noget gik galt',
      description: 'Prøv venligst igen senere',
    },
    variantPrice: {
      savedAmount: 'Spar',
    },
    gearTypes: {
      [GearType.Automatic]: 'Automatisk',
      [GearType.Manual]: 'Manuel',
    },
    colorTypes: {
      [ColorType.Base]: 'Farve',
      [ColorType.Roof]: 'Tagfarve',
      [ColorType.ColorPack]: 'Farvepakke',
    },
    fuelTypes: {
      [FuelType.Diesel]: 'Diesel',
      [FuelType.Electric]: 'Elektrisk',
      [FuelType.Hybrid]: 'Plug-in hybrid',
      [FuelType.Petrol]: 'Benzin',
      [FuelType.MildHybrid]: 'Mild hybrid',
      [FuelType.EREV]: 'EREV',
    },
    noVariants: {
      title: 'Priser kan ikke hentes',
      description: 'Kontakt din nærmeste forhandler for mere information.',
    },
    months: 'months',
  },
  tableFields: {
    price: 'Pris',
    fuelType: 'Brændstof',
    engine: 'Motor',
    horsePowers: 'Hestekræfter',
    gearBox: 'Gearkasse',
    consumption: 'Forbrug',
    energyClass: 'Energiklasse',
    ownerTax: 'Grøn afgift',
    emission: <EmissionLabel />,
    batteryCapacity: 'Batteri',
    drivingRange: 'Rækkevidde',
    period: 'Løbetid (md.)',
    downPayment: 'Udbetaling',
    kmPerYear: 'Kilometer pr. år',
    monthlyPrice: 'Månedlig ydelse',
    totalMinimumPrice: 'Betaling i bindingsperioden',
    totalPrice: 'Betaling i perioden',
  },
  controls: {
    configureCar: 'Konfigurer din bil',
    bookCarOnline: 'Bestil bil online',
    downloadPriceList: 'Download den fulde prisliste',
  },
};

export default DK;
